"use client";

import { useEffect } from "react";

const SCROLL_LABEL: Record<string, string> = {
  de: "Tabelle, seitlich scrollbar",
  en: "Table, scrolls sideways",
  da: "Tabel, kan rulles vandret",
  ar: "جدول، يمكن تمريره أفقيًا",
  uk: "Таблиця, прокручується горизонтально",
  ru: "Таблица, прокручивается вбок",
  vi: "Bảng, cuộn ngang để xem",
  es: "Tabla con desplazamiento lateral",
};

/**
 * Copies each article table's thead labels into `data-label` on every body cell
 * and wraps each value in `span.tv`. globals.css hides the thead on mobile and
 * lays every card row out as label | value, so both must exist or column
 * context is lost. Idempotent, runs once.
 *
 * Wide tables scroll sideways inside `.table-wrap` from 641px up. A scrolling
 * area has to be reachable by keyboard (WCAG 2.1.1, axe
 * scrollable-region-focusable), so a wrap that actually overflows becomes a
 * labelled, focusable region; one that fits (or is stacked as cards on a
 * phone) stays out of the tab order.
 */
export function TablesToCards() {
  useEffect(() => {
    const root = document.querySelector(".article-prose");
    if (!root) return;
    const tables = root.querySelectorAll("table");
    for (const table of tables) {
      const headerCells = Array.from(table.querySelectorAll("thead th")).map(
        (th) => (th.textContent || "").trim(),
      );
      if (headerCells.length === 0) continue;
      // Five or more columns never fit the text column: they become a card
      // grid on every screen (globals.css, table.wide) instead of scrolling.
      if (headerCells.length >= 5) table.classList.add("wide");
      const rows = table.querySelectorAll("tbody tr");
      for (const row of rows) {
        const cells = row.querySelectorAll("td");
        cells.forEach((cell, i) => {
          if (headerCells[i] && !cell.hasAttribute("data-label")) {
            cell.setAttribute("data-label", headerCells[i]);
          }
          // On a phone a labelled cell is a two-column grid (label | value).
          // Mixed content — a link followed by text — would otherwise become
          // several grid items and wrap onto separate rows, so the value is
          // wrapped once in a single span. The first cell is the card heading.
          if (i > 0 && !cell.querySelector(":scope > .tv")) {
            const value = document.createElement("span");
            value.className = "tv";
            while (cell.firstChild) value.appendChild(cell.firstChild);
            cell.appendChild(value);
          }
        });
      }
    }

    const lang = document.documentElement.lang.slice(0, 2);
    const label = SCROLL_LABEL[lang] ?? SCROLL_LABEL.de;
    const wraps = Array.from(root.querySelectorAll<HTMLElement>(".table-wrap"));
    const update = () => {
      for (const wrap of wraps) {
        if (wrap.scrollWidth > wrap.clientWidth + 1) {
          wrap.tabIndex = 0;
          wrap.setAttribute("role", "region");
          wrap.setAttribute("aria-label", label);
        } else {
          wrap.removeAttribute("tabindex");
          wrap.removeAttribute("role");
          wrap.removeAttribute("aria-label");
        }
      }
    };
    update();
    // Watch the tables too: a wrap keeps its size while fonts and styles
    // arrive, but the table inside grows past it — only then does it scroll.
    const observer = new ResizeObserver(update);
    wraps.forEach((wrap) => {
      observer.observe(wrap);
      const table = wrap.querySelector("table");
      if (table) observer.observe(table);
    });
    return () => observer.disconnect();
  }, []);
  return null;
}
